import {createSlice} from "@reduxjs/toolkit";
import axios from "axios";
import apiService from "../../api/apiService";
import {formatAxiosErrorMessage, qargsToQstring} from "../../utils/common";
import {endPoints} from "../../api/endpoints";
import {apiUrlBuilder} from "../../utils/util";
import {clientRefreshed, clientUpdateSuccess} from "../client/clientReducer";

let activeLeadListRequest = null;
let activeKanbanLeadRequest = null;

const cancelActiveLeadRequests = () => {
    if (activeLeadListRequest) {
        activeLeadListRequest.cancel("Superseded lead list request");
        activeLeadListRequest = null;
    }
    if (activeKanbanLeadRequest) {
        activeKanbanLeadRequest.cancel("Superseded kanban lead request");
        activeKanbanLeadRequest = null;
    }
};

/** Owner id used on lead list / kanban cards (client.user). */
const kanbanLeadOwnerId = (lead) => {
    if (lead?.client?.user?.id != null) return lead.client.user.id;
    const user = lead?.user;
    if (user && typeof user === "object") return user.id ?? null;
    return user ?? null;
};

/** Shallow-merge updated client into a lead row; keeps list-only nested fields. */
const mergeClientOntoLead = (lead, updatedClient) => {
    if (lead?.client?.id !== updatedClient.id) return lead;
    return {
        ...lead,
        client: {
            ...lead.client,
            ...updatedClient,
        },
    };
};

/** Patch matching lead.client on list results and kanban lanes. */
const patchClientInLeadStores = (state, updatedClient) => {
    if (!updatedClient?.id) return;

    if (state.leadGet?.data?.results) {
        state.leadGet.data.results = state.leadGet.data.results.map((lead) =>
            mergeClientOntoLead(lead, updatedClient)
        );
    }

    if (state.kanbanLeads?.data) {
        Object.keys(state.kanbanLeads.data).forEach((statusId) => {
            const lane = state.kanbanLeads.data[statusId];
            if (!lane?.leads) return;
            lane.leads = lane.leads.map((lead) =>
                mergeClientOntoLead(lead, updatedClient)
            );
        });
    }
};

const LeadInitialState = {
    leadGet: {
        isLoading: false,
        data: null,
        error: null,
    },
    kanbanLeads: {
        isLoading: false,
        data: null,
        error: null,
    },
    leadRetrieve: {
        isLoading: false,
        data: null,
        error: null,
    },
    calendarAppointment: {
        isLoading: false,
        data: null,
        error: null,
    },
    calendarTask: {
        isLoading: false,
        data: null,
        error: null,
    },

    leadStat: {
        isLoading: false,
        data: null,
        error: null,
    },

    leadUsers: {
        isLoading: false,
        data: null,
        error: null,
    },

    leadUpdate: {
        isLoading: false,
        data: null,
        error: null,
    },
    leadDelete: {
        isLoading: false,
        isDeleted: false,
        error: null,
    },
    export: {
        isLoading: false,
        data: false,
        error: null,
    },
    growth: {
        isLoading: false,
        data: null,
        error: null,
    },
    lead_chart: {
        isLoading: false,
        data: null,
        error: null,
    },
    bulkImport: {
        isLoading: false,
        success: false,
        error: null,
    },
    bulkImportSampleDownload: {
        isLoading: false,
        error: null,
        success: false,
    },
    bulkDelete: {
        isLoading: false,
        success: false,
        error: null,
    },
    bulkStatusUpdate: {
        isLoading: false,
        success: false,
        error: null,
    },
    bulkReassign: {
        isLoading: false,
        success: false,
        error: null,
    },
    // Distinct lead source values for campaign audience filters
    leadSources: {
        isLoading: false,
        data: null,
        error: null,
    },
};

export const LeadSlice = createSlice({
    name: "lead",
    initialState: {
        ...LeadInitialState,
    },
    reducers: {
        GetLeadRequesting: (state, action) => {
            state.leadGet.isLoading = true;
        },
        GetLeadSuccess: (state, action) => {
            state.leadGet.isLoading = false;
            state.leadGet.error = null;
            state.leadGet.data = action.payload.data;
        },
        GetLeadError: (state, action) => {
            state.leadGet.isLoading = false;
            state.leadGet.error = action.payload;
        },
        GetLeadReset: (state, action) => {
            state.leadGet = LeadInitialState.leadGet;
        },

        CalendarAppointmentRequesting: (state, action) => {
            state.calendarAppointment.isLoading = true;
        },
        CalendarAppointmentSuccess: (state, action) => {
            state.calendarAppointment.isLoading = false;
            state.calendarAppointment.error = null;
            state.calendarAppointment.data = action.payload.data;
        },
        CalendarAppointmentError: (state, action) => {
            state.calendarAppointment.isLoading = false;
            state.calendarAppointment.error = action.payload;
        },
        CalendarAppointmentReset: (state, action) => {
            state.calendarAppointment = LeadInitialState.calendarAppointment;
        },

        CalendarTaskRequesting: (state, action) => {
            state.calendarTask.isLoading = true;
        },
        CalendarTaskSuccess: (state, action) => {
            state.calendarTask.isLoading = false;
            state.calendarTask.error = null;
            state.calendarTask.data = action.payload.data;
        },
        CalendarTaskError: (state, action) => {
            state.calendarTask.isLoading = false;
            state.calendarTask.error = action.payload;
        },
        CalendarTaskReset: (state, action) => {
            state.calendarTask = LeadInitialState.calendarTask;
        },

        LeadStatRequesting: (state, action) => {
            state.leadStat.isLoading = true;
        },
        LeadStatSuccess: (state, action) => {
            state.leadStat.isLoading = false;
            state.leadStat.error = null;
            state.leadStat.data = action.payload.data;
        },
        LeadStatError: (state, action) => {
            state.leadStat.isLoading = false;
            state.leadStat.error = action.payload;
        },
        LeadStatReset: (state, action) => {
            state.leadStat = LeadInitialState.leadStat;
        },

        LeadUsersRequesting: (state, action) => {
            state.leadUsers.isLoading = true;
        },
        LeadUsersSuccess: (state, action) => {
            state.leadUsers.isLoading = false;
            state.leadUsers.error = null;
            state.leadUsers.data = action.payload.data;
        },
        LeadUsersError: (state, action) => {
            state.leadUsers.isLoading = false;
            state.leadUsers.error = action.payload;
        },
        LeadUsersReset: (state, action) => {
            state.leadUsers = LeadInitialState.leadRetrieve;
        },

        LeadUpdateRequesting: (state, action) => {
            state.leadUpdate.isLoading = true;
        },
        LeadUpdateSuccess: (state, action) => {
            state.leadUpdate.isLoading = false;
            state.leadUpdate.error = null;
            state.leadUpdate.data = action.payload.data;
            // Keep list row in sync when this lead is on the current page
            const updated = action.payload.data;
            if (updated?.id != null && state.leadGet?.data?.results) {
                const idx = state.leadGet.data.results.findIndex(
                    (lead) => lead.id === updated.id
                );
                if (idx !== -1) {
                    state.leadGet.data.results[idx] = updated;
                }
            }
        },
        LeadUpdateError: (state, action) => {
            state.leadUpdate.isLoading = false;
            state.leadUpdate.error = action.payload;
        },
        LeadUpdateReset: (state, action) => {
            state.leadUpdate = LeadInitialState.leadUpdate;
        },

        LeadRetrieveRequesting: (state, action) => {
            state.leadRetrieve.isLoading = true;
        },
        LeadRetrieveSuccess: (state, action) => {
            state.leadRetrieve.isLoading = false;
            state.leadRetrieve.error = null;
            state.leadRetrieve.data = action.payload.data;
        },
        LeadRetrieveError: (state, action) => {
            state.leadRetrieve.isLoading = false;
            state.leadRetrieve.error = action.payload;
            state.leadRetrieve.data = null;
        },
        LeadRetrieveReset: (state, action) => {
            state.leadRetrieve = LeadInitialState.leadRetrieve;
        },

        LeadDeleteRequesting: (state, action) => {
            state.leadDelete.isLoading = true;
        },
        LeadDeleteSuccess: (state, action) => {
            state.leadDelete.isLoading = false;
            state.leadDelete.error = null;
            state.leadDelete.isDeleted = true;
        },
        LeadDeleteError: (state, action) => {
            state.leadDelete.isLoading = false;
            state.leadDelete.error = action.payload;
        },
        LeadDeleteReset: (state, action) => {
            state.leadDelete = LeadInitialState.leadDelete;
        },

        LeadExportRequesting: (state, action) => {
            state.export.isLoading = true;
        },
        LeadExportSuccess: (state, action) => {
            state.export.isLoading = false;
            state.export.error = null;
            state.export.data = "files";
        },
        LeadExportError: (state, action) => {
            state.export.isLoading = false;
            state.export.error = action.payload;
        },
        LeadExportReset: (state, action) => {
            state.export = LeadInitialState.export;
        },

        LeadGrowthRequesting: (state, action) => {
            state.growth.isLoading = true;
        },
        LeadGrowthSuccess: (state, action) => {
            state.growth.isLoading = false;
            state.growth.error = null;
            state.growth.data = action.payload.data;
        },
        LeadGrowthError: (state, action) => {
            state.growth.isLoading = false;
            state.growth.error = action.payload;
        },
        LeadGrowthReset: (state, action) => {
            state.growth = LeadInitialState.growth;
        },


        LeadChartRequesting: (state, action) => {
            state.lead_chart.isLoading = true;
        },
        LeadChartSuccess: (state, action) => {
            state.lead_chart.isLoading = false;
            state.lead_chart.error = null;
            state.lead_chart.data = action.payload.data;
        },
        LeadChartError: (state, action) => {
            state.lead_chart.isLoading = false;
            state.lead_chart.error = action.payload;
        },
        LeadChartReset: (state, action) => {
            state.lead_chart = LeadInitialState.lead_chart;
        },
        BulkImportRequesting: (state) => {
            state.bulkImport.isLoading = true;
            state.bulkImport.success = false;
            state.bulkImport.error = null;
        },
        BulkImportSuccess: (state) => {
            state.bulkImport.isLoading = false;
            state.bulkImport.success = true;
        },
        BulkImportError: (state, action) => {
            state.bulkImport.isLoading = false;
            state.bulkImport.error = action.payload;
            state.bulkImport.success = false;
        },
        BulkImportReset: (state) => {
            state.bulkImport = LeadInitialState.bulkImport;
        },
        BulkImportSampleDownloadRequesting: (state) => {
            state.bulkImportSampleDownload.isLoading = true;
            state.bulkImportSampleDownload.error = null;
            state.bulkImportSampleDownload.success = false;
        },
        BulkImportSampleDownloadSuccess: (state) => {
            state.bulkImportSampleDownload.isLoading = false;
            state.bulkImportSampleDownload.success = true;
        },
        BulkImportSampleDownloadError: (state, action) => {
            state.bulkImportSampleDownload.isLoading = false;
            state.bulkImportSampleDownload.error = action.payload;
            state.bulkImportSampleDownload.success = false;
        },
        BulkImportSampleDownloadReset: (state) => {
            state.bulkImportSampleDownload = LeadInitialState.bulkImportSampleDownload;
        },
        BulkDeleteRequesting: (state) => {
            state.bulkDelete.isLoading = true;
            state.bulkDelete.success = false;
            state.bulkDelete.error = null;
        },
        BulkDeleteSuccess: (state) => {
            state.bulkDelete.isLoading = false;
            state.bulkDelete.success = true;
        },
        BulkDeleteError: (state, action) => {
            state.bulkDelete.isLoading = false;
            state.bulkDelete.error = action.payload;
            state.bulkDelete.success = false;
        },
        BulkDeleteReset: (state) => {
            state.bulkDelete = LeadInitialState.bulkDelete;
        },
        BulkStatusUpdateRequesting: (state) => {
            state.bulkStatusUpdate.isLoading = true;
            state.bulkStatusUpdate.success = false;
            state.bulkStatusUpdate.error = null;
        },
        BulkStatusUpdateSuccess: (state) => {
            state.bulkStatusUpdate.isLoading = false;
            state.bulkStatusUpdate.success = true;
        },
        BulkStatusUpdateError: (state, action) => {
            state.bulkStatusUpdate.isLoading = false;
            state.bulkStatusUpdate.error = action.payload;
            state.bulkStatusUpdate.success = false;
        },
        BulkStatusUpdateReset: (state) => {
            state.bulkStatusUpdate = LeadInitialState.bulkStatusUpdate;
        },
        // Bulk reassign selected leads to a target user
        BulkReassignRequesting: (state) => {
            state.bulkReassign.isLoading = true;
            state.bulkReassign.success = false;
            state.bulkReassign.error = null;
        },
        BulkReassignSuccess: (state) => {
            state.bulkReassign.isLoading = false;
            state.bulkReassign.success = true;
        },
        BulkReassignError: (state, action) => {
            state.bulkReassign.isLoading = false;
            state.bulkReassign.error = action.payload;
            state.bulkReassign.success = false;
        },
        BulkReassignReset: (state) => {
            state.bulkReassign = LeadInitialState.bulkReassign;
        },

        LeadSourcesRequesting: (state) => {
            state.leadSources.isLoading = true;
            state.leadSources.error = null;
        },
        LeadSourcesSuccess: (state, action) => {
            state.leadSources.isLoading = false;
            state.leadSources.error = null;
            state.leadSources.data = action.payload.data;
        },
        LeadSourcesError: (state, action) => {
            state.leadSources.isLoading = false;
            state.leadSources.error = action.payload;
        },
        LeadSourcesReset: (state) => {
            state.leadSources = LeadInitialState.leadSources;
        },

        KanbanLeadsRequesting: (state) => {
            state.kanbanLeads.isLoading = true;
        },
        KanbanLeadsSuccess: (state, action) => {
            state.kanbanLeads.isLoading = false;
            state.kanbanLeads.error = null;
            const laneResponse = action.payload.data;
            if(!state.kanbanLeads.data || Object.keys(laneResponse).length > 1){
                state.kanbanLeads.data = laneResponse;
            }
            else{
                const updated = JSON.parse(JSON.stringify(state.kanbanLeads.data));
                Object.keys(laneResponse).forEach((statusId) =>{
                    const prevLane = updated[statusId] || { };
                    const newLane = laneResponse[statusId];
                    const combinedLeads = [...(prevLane.leads || []), ...(newLane.leads || [])];
                    const uniqueLeadsMap = {};
                    combinedLeads.forEach((lead) => {
                        uniqueLeadsMap[lead.id] = lead;
                    });
                    const uniqueLeads = Object.values(uniqueLeadsMap);
                    const currentPage = Math.ceil(uniqueLeads.length / 10) || 1;
                    updated[statusId] = {
                        ...newLane,
                        total: newLane.total, 
                        has_more: newLane.has_more,
                        leads: uniqueLeads,
                        currentPage: currentPage
                    }
                })
                state.kanbanLeads.data = updated;

            }
        },
        KanbanLeadsError: (state, action) => {
            state.kanbanLeads.isLoading = false;
            state.kanbanLeads.error = action.payload;
        },
        KanbanLeadsReset: (state) => {
            state.kanbanLeads = LeadInitialState.kanban;
        },
        KanbanLeadStatusUpdate: (state, action) => {
            const payload = action.payload || {};
            const lead = payload.lead ?? payload;
            const isOrgView = Boolean(payload.isOrgView);
            const {id, status} = lead;

            if (!state.kanbanLeads.data || !id) return;

            // Find the lane where the lead currently exists
            let currentLaneId = null;
            Object.keys(state.kanbanLeads.data).forEach((statusId) => {
                const lane = state.kanbanLeads.data[statusId];
                if (lane.leads?.some((item) => item.id === id)) {
                    currentLaneId = statusId;
                }
            });

            if (!currentLaneId) return; // Lead not found

            const currentLane = state.kanbanLeads.data[currentLaneId];
            const leadIndex = currentLane.leads.findIndex((item) => item.id === id);
            const leadToMove = currentLane.leads[leadIndex];
            const nextStatusId = status?.id ?? status;
            const prevOwnerId = kanbanLeadOwnerId(leadToMove);
            const nextOwnerId = kanbanLeadOwnerId(lead);
            const ownerChanged =
                prevOwnerId != null &&
                nextOwnerId != null &&
                String(prevOwnerId) !== String(nextOwnerId);

            // Personal / user view only shows this scope's leads — drop the
            // card after reassign. Org view keeps it on the board.
            if (!isOrgView && ownerChanged) {
                currentLane.leads.splice(leadIndex, 1);
                currentLane.total = currentLane?.total
                    ? currentLane.total - 1
                    : currentLane.leads.length;
                return;
            }

            // Reassign / source / other in-place PATCHes keep the same column —
            // replace the card where it is so it does not jump to the top.
            if (String(currentLaneId) === String(nextStatusId)) {
                currentLane.leads[leadIndex] = lead;
                return;
            }

            // Status drag: skip if the destination lane is missing so we never
            // splice the card out with nowhere to put it.
            const newLane = state.kanbanLeads.data[nextStatusId]
                ?? state.kanbanLeads.data[String(nextStatusId)];
            if (!newLane) return;

            // Remove from current lane
            currentLane.leads.splice(leadIndex, 1);
            currentLane.total = currentLane?.total ? currentLane.total - 1 : currentLane.leads.length;

            newLane.leads = [lead, ...newLane.leads];
            newLane.total = newLane?.total ? newLane.total + 1 : newLane.leads.length;
            state.kanbanLeads.data[nextStatusId] = newLane;
        },
        ResetWhatsappNotification: (state, action) => {
            const {leadId} = action.payload;
            // Update leadGet list
            if (state.leadGet?.data?.results) {
                state.leadGet.data.results = state.leadGet.data.results.map((lead) => {
                    if (lead.id === leadId) {
                        return {
                            ...lead,
                            client: {
                                ...lead.client,
                                unread_notifications: {
                                    ...lead.client.unread_notifications,
                                    whatsapp: 0,
                                },
                            },
                        };
                    }
                    return lead;
                });
            }


            // Update leadRetrieve if it matches
            if (state.leadRetrieve?.data && state.leadRetrieve?.data?.id === leadId && state.leadRetrieve?.data?.client) {
                state.leadRetrieve.data.client = {
                    ...state.leadRetrieve.data.client,
                    unread_notifications: {
                        ...state.leadRetrieve.data.client.unread_notifications,
                        whatsapp: 0,
                    },
                };
            }

            // Update kanbanLeads if present
            if (state.kanbanLeads.data) {
                Object.keys(state.kanbanLeads.data).forEach((statusId) => {
                    const lane = state.kanbanLeads.data[statusId];
                    lane.leads = lane.leads.map((lead) => {
                        if (lead.id === leadId) {
                            return {
                                ...lead,
                                client: {
                                    ...lead.client,
                                    unread_notifications: {
                                        ...lead.client.unread_notifications,
                                        whatsapp: 0,
                                    },
                                },
                            };
                        }
                        return lead;
                    });
                });
            }
        },


        LeadMasterReset: () => LeadInitialState,
    },
    // When client details are patched elsewhere, refresh nested client on list/kanban
    extraReducers: (builder) => {
        builder.addCase(clientUpdateSuccess, (state, action) => {
            patchClientInLeadStores(state, action.payload.data);
        });
        // Silent client refetch (e.g. after recording a payment): update amount/balance on cards
        builder.addCase(clientRefreshed, (state, action) => {
            patchClientInLeadStores(state, action.payload);
            // Lead drawer (ClientDetails by leadId) reads the client from leadRetrieve
            const retrieved = state.leadRetrieve?.data;
            if (retrieved?.client) {
                state.leadRetrieve.data = mergeClientOntoLead(retrieved, action.payload);
            } else if (retrieved?.data?.client) {
                state.leadRetrieve.data.data = mergeClientOntoLead(retrieved.data, action.payload);
            }
        });
    },
});

export const getLeadAPI = (query) => async (dispatch) => {
    dispatch(GetLeadRequesting());
    let stringQuery = qargsToQstring(query);
    cancelActiveLeadRequests();
    const cancelSource = axios.CancelToken.source();
    activeLeadListRequest = cancelSource;
    apiService
        .get(`${endPoints.lead.get}${stringQuery}`, {
            cancelToken: cancelSource.token,
        })
        .then((response) => {
            // Ignore superseded responses — only the latest filter request applies
            if (activeLeadListRequest !== cancelSource) {
                return;
            }
            activeLeadListRequest = null;
            dispatch(GetLeadSuccess(response.data));
        })
        .catch((error) => {
            if (axios.isCancel(error)) {
                return;
            }
            if (activeLeadListRequest !== cancelSource) {
                return;
            }
            activeLeadListRequest = null;
            dispatch(GetLeadError(formatAxiosErrorMessage(error)));
        });
};

export const getCalendarAppointmentAPI = (query) => async (dispatch) => {
    dispatch(CalendarAppointmentRequesting());
    let stringQuery = qargsToQstring(query);
    apiService
        .get(`${endPoints.lead.calendarAppointment}${stringQuery}`, {})
        .then((response) => {
            dispatch(CalendarAppointmentSuccess(response.data));
        })
        .catch((error) =>
            dispatch(CalendarAppointmentError(formatAxiosErrorMessage(error)))
        );
};

export const getCalendarTaskAPI = (query) => async (dispatch) => {
    dispatch(CalendarTaskRequesting());
    let stringQuery = qargsToQstring(query);
    apiService
        .get(`${endPoints.lead.calendarTask}${stringQuery}`, {})
        .then((response) => {
            dispatch(CalendarTaskSuccess(response.data));
        })
        .catch((error) =>
            dispatch(CalendarTaskError(formatAxiosErrorMessage(error)))
        );
};

export const getLeadStatAPI = (query) => async (dispatch) => {
    dispatch(LeadStatRequesting());
    let stringQuery = qargsToQstring(query);
    apiService
        .get(`${endPoints.lead.leadStat}${stringQuery}`, {})
        .then((response) => {
            dispatch(LeadStatSuccess(response.data));
        })
        .catch((error) => dispatch(LeadStatError(formatAxiosErrorMessage(error))));
};

export const getLeadUsersAPI = (query) => async (dispatch) => {
    dispatch(LeadUsersRequesting());
    let stringQuery = qargsToQstring(query);
    apiService
        .get(`${endPoints.lead.leadUsers}${stringQuery}`, {})
        .then((response) => {
            dispatch(LeadUsersSuccess(response.data));
        })
        .catch((error) => dispatch(LeadUsersError(formatAxiosErrorMessage(error))));
};

export const UpdateLeadAPI = (data) => async (dispatch) => {
    dispatch(LeadUpdateRequesting());
    apiService
        .patch(`${endPoints.lead.get}${data.id}/`, data)
        .then((response) => {
            dispatch(LeadUpdateSuccess(response.data));
            // Toast(toastMessages.leadUpdatedSuccess, "success");
        })
        .catch((error) => {
            // Toast(toastMessages.leadUpdatedFailed, "disappear-error");
            dispatch(LeadUpdateError(formatAxiosErrorMessage(error)));
        });
};

export const RetrieveLeadAPI = (id) => async (dispatch) => {
    dispatch(LeadRetrieveRequesting());
    apiService
        .get(`${endPoints.lead.get}${id}/`, {})
        .then((response) => {
            dispatch(LeadRetrieveSuccess(response.data));
        })
        .catch((error) => {
            dispatch(LeadRetrieveError(formatAxiosErrorMessage(error)));
        });
};

export const DeleteLeadApi = (id) => async (dispatch) => {
    dispatch(LeadDeleteRequesting());
    apiService
        .delete(`${endPoints.lead.get}${id}/`, {})
        .then((response) => {
            dispatch(LeadDeleteSuccess(response.data));
        })
        .catch((error) => {
            dispatch(LeadDeleteError(formatAxiosErrorMessage(error)));
        });
};

export const ExportLeadApi = (query = {}) => async (dispatch) => {
    dispatch(LeadExportRequesting());
    // console.log(query)
    apiService
        .get(apiUrlBuilder(endPoints.lead.export, null, query), {
            responseType: 'blob',
            "Access-Control-Expose-Headers": '*',
        })
        .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'export_lead.csv');
            document.body.appendChild(link);
            link.click();
            dispatch(LeadExportSuccess())
        })
        .catch((error) =>
            dispatch(LeadExportError(formatAxiosErrorMessage(error)))
        );
};

export const LeadGrowthAPI = () => async (dispatch) => {
    dispatch(LeadGrowthRequesting());
    apiService
        .get(`${endPoints.lead.growth}`, {})
        .then((response) => {
            dispatch(LeadGrowthSuccess(response.data));
        })
        .catch((error) => {
            dispatch(LeadGrowthError(formatAxiosErrorMessage(error)));
        });
};

/** Optional query: { start_date, end_date } — omit for default chart window */
export const LeadChartAPI = (query = {}) => async (dispatch) => {
    dispatch(LeadChartRequesting());
    const stringQuery = qargsToQstring(query || {});
    apiService
        .get(`${endPoints.lead.lead_chart}${stringQuery}`, {})
        .then((response) => {
            dispatch(LeadChartSuccess(response.data));
        })
        .catch((error) => {
            dispatch(LeadChartError(formatAxiosErrorMessage(error)));
        });
};
export const LeadBulkImportAPI = (formData) => async (dispatch) => {
    dispatch(BulkImportRequesting());
    try {
        await apiService.post(`${endPoints.lead.bulkImport}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        dispatch(BulkImportSuccess());
    } catch (error) {
        dispatch(BulkImportError(formatAxiosErrorMessage(error)));
    }
};
export const DownloadSampleCSVAPI = () => async (dispatch) => {
    dispatch(BulkImportSampleDownloadRequesting());
    try {
        const response = await apiService.get(`${endPoints.lead.bulkImportSampleCSV}`, {
            responseType: "blob",
        });

        const blob = new Blob([response.data], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "sample_leads_import.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        dispatch(BulkImportSampleDownloadSuccess());
    } catch (error) {
        dispatch(BulkImportSampleDownloadError(formatAxiosErrorMessage(error)));
    }
};

export const LeadBulkDeleteAPI = (payload) => async (dispatch) => {
    dispatch(BulkDeleteRequesting());
    try {
        await apiService.post(`${endPoints.lead.bulkDelete}`, payload);
        dispatch(BulkDeleteSuccess());
    } catch (error) {
        dispatch(BulkDeleteError(formatAxiosErrorMessage(error)));
    }
};
export const LeadBulkStatusUpdateAPI = (payload) => async (dispatch) => {
    dispatch(BulkStatusUpdateRequesting());
    try {
        await apiService.post(`${endPoints.lead.bulkUpdateStatus}`, payload);
        dispatch(BulkStatusUpdateSuccess());
    } catch (error) {
        dispatch(BulkStatusUpdateError(formatAxiosErrorMessage(error)));
    }
};

export const LeadBulkReassignAPI = (payload) => async (dispatch) => {
    dispatch(BulkReassignRequesting());
    try {
        await apiService.post(`${endPoints.lead.bulkReassign}`, payload);
        dispatch(BulkReassignSuccess());
    } catch (error) {
        dispatch(BulkReassignError(formatAxiosErrorMessage(error)));
    }
};

export const getKanbanLeadAPI = (query) => async (dispatch) => {
    dispatch(KanbanLeadsRequesting());
    let stringQuery = qargsToQstring(query);
    cancelActiveLeadRequests();
    const cancelSource = axios.CancelToken.source();
    activeKanbanLeadRequest = cancelSource;
    apiService
        .get(`${endPoints.lead.get}kanban/${stringQuery}`, {
            cancelToken: cancelSource.token,
        })
        .then((response) => {
            // Ignore superseded responses — only the latest filter request applies
            if (activeKanbanLeadRequest !== cancelSource) {
                return;
            }
            activeKanbanLeadRequest = null;
            dispatch(KanbanLeadsSuccess(response.data));
        })
        .catch((error) => {
            if (axios.isCancel(error)) {
                return;
            }
            if (activeKanbanLeadRequest !== cancelSource) {
                return;
            }
            activeKanbanLeadRequest = null;
            dispatch(KanbanLeadsError(formatAxiosErrorMessage(error)));
        });
};

export const {
    GetLeadRequesting,
    GetLeadSuccess,
    GetLeadError,
    GetLeadReset,

    CalendarAppointmentRequesting,
    CalendarTaskRequesting,
    CalendarAppointmentReset,
    CalendarTaskSuccess,
    CalendarAppointmentError,
    CalendarTaskReset,
    CalendarTaskError,
    CalendarAppointmentSuccess,

    LeadStatRequesting,
    LeadStatSuccess,
    LeadStatError,
    LeadStatReset,

    LeadUsersRequesting,
    LeadUsersSuccess,
    LeadUsersError,
    LeadUsersReset,

    LeadUpdateRequesting,
    LeadUpdateSuccess,
    LeadUpdateError,
    LeadUpdateReset,

    LeadRetrieveRequesting,
    LeadRetrieveSuccess,
    LeadRetrieveError,
    LeadRetrieveReset,

    LeadDeleteRequesting,
    LeadDeleteSuccess,
    LeadDeleteError,
    LeadDeleteReset,

    LeadExportRequesting,
    LeadExportSuccess,
    LeadExportError,
    LeadExportReset,

    LeadGrowthRequesting,
    LeadGrowthSuccess,
    LeadGrowthError,
    LeadGrowthReset,

    LeadChartRequesting,
    LeadChartSuccess,
    LeadChartError,
    LeadChartReset,

    BulkImportRequesting,
    BulkImportSuccess,
    BulkImportError,
    BulkImportReset,

    BulkImportSampleDownloadRequesting,
    BulkImportSampleDownloadSuccess,
    BulkImportSampleDownloadError,
    BulkImportSampleDownloadReset,

    BulkDeleteRequesting,
    BulkDeleteSuccess,
    BulkDeleteError,
    BulkDeleteReset,

    BulkStatusUpdateRequesting,
    BulkStatusUpdateSuccess,
    BulkStatusUpdateError,
    BulkStatusUpdateReset,

    BulkReassignRequesting,
    BulkReassignSuccess,
    BulkReassignError,
    BulkReassignReset,

    LeadSourcesRequesting,
    LeadSourcesSuccess,
    LeadSourcesError,
    LeadSourcesReset,

    KanbanLeadsRequesting,
    KanbanLeadsSuccess,
    KanbanLeadsError,
    KanbanLeadsReset,
    KanbanLeadStatusUpdate,

    LeadMasterReset,
    ResetWhatsappNotification,
} = LeadSlice.actions;

// GET /leads/sources/ — returns ["facebook", "google", ...] (or { data: [...] })
export const GetLeadSourcesAPI = () => async (dispatch) => {
    dispatch(LeadSourcesRequesting());
    apiService
        .get(apiUrlBuilder(endPoints.lead.sources, null, {}), {})
        .then((response) => {
            const body = response.data;
            const sources = Array.isArray(body) ? body : (body?.data ?? []);
            dispatch(LeadSourcesSuccess({ data: sources }));
        })
        .catch((error) =>
            dispatch(LeadSourcesError(formatAxiosErrorMessage(error)))
        );
};

export const LeadState = (state) => state.lead;
export default LeadSlice.reducer;
